import React from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Icon } from '../../icons/Icon'
import { spinnerContract } from './spinner.contract'

const VARIANTS = spinnerContract.props.variant.values

export type SpinnerVariant = (typeof VARIANTS)[number]

type ContractCell = { container: string; icon: string }

const cellFor = (variant: SpinnerVariant): ContractCell => {
    const key = `variant=${variant}`
    const found = (spinnerContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds Spinner: the Figma contract has no "${key}"`)
    }
    return found
}

export type SpinnerProps = {
    /** Figma's two: `default` inks the arc `primary/default`, `on-button` `content/on-primary`. */
    variant?: SpinnerVariant
    className?: string
    accessibilityLabel?: string
}

/**
 * The one component in this package that *is* a shape rather than has one.
 *
 * Figma draws a single arc and nothing else — no fill, no stroke, no box — and
 * the dumper skips a vector's fill on purpose, because reading it as a surface
 * gave the contract `bg-primary-default`: a solid 24x24 square where the design
 * draws a line. Correctly refusing to call it a surface left nothing at all, so
 * the contract now carries the path itself and the arc renders `currentColor`
 * like every other glyph here.
 *
 * The rotation is not from Figma. The file draws one frame, and animation is
 * behaviour — `animate-spin` is the web's own default for exactly this, at the
 * same 1s linear turn gluestack's spinner uses.
 */
export const Spinner = ({ variant = 'default', className, accessibilityLabel }: SpinnerProps) => {
    const cell = cellFor(variant)
    return (
        <View
            role='progressbar'
            accessibilityLabel={accessibilityLabel}
            className={twMerge('animate-spin', cell.container, className)}
            testID={`spinner-${variant}`}
        >
            <Icon glyph={spinnerContract.glyph} className={cell.icon} />
        </View>
    )
}

Spinner.displayName = 'Spinner'

export { VARIANTS as SPINNER_VARIANTS }
